/**
 * Solar 발전소의 모든 Entity(Array, String, Module)를 통합 관리하는 클래스
 *
 * === ID 체계 (PanelEntityId) ===
 * 각 Entity는 PanelEntityId 값 객체를 통해 식별된다.
 *
 *   entityId.selfSeq        — DB seq (유니크, 객체 구분·Map key)
 *   entityId.selfDisplayId  — 화면 표시 번호 (라벨·검색용)
 *   entityId.entityType     — 'array' | 'string' | 'module'
 *   entityId.parentSeq      — 직계 부모 seq
 *   entityId.mapKey         — "${type}_${seq}" (EntityManager Map lookup)
 *
 * === 사용 규칙 ===
 * - 객체 비교:  항상 entityId.selfSeq로 비교 (entityId.equals() 사용)
 * - Map key:   항상 entityId.mapKey 사용 (직접 문자열 조합 금지)
 * - 표시:      entityId.displayLabel 또는 entityId.selfDisplayId 사용
 * - 계층 탐색: entityId.parentSeq / entityId.parentDisplayId 사용
 */
import { PanelEntityId } from '@/features/panel-planning/utils/PanelEntityId';
import { readStringDisplayNo, readStringSeq } from '@/features/panel-planning/utils/panelMapFeatureProps';

export class SolarEntity {
  /**
   * @param {Object} config
   * @param {PanelEntityId} config.entityId  — PanelEntityId 인스턴스 (필수)
   * @param {string}        [config.name]    — 표시명 (없으면 entityId.displayLabel)
   * @param {Array}         [config.children]     — 자식 Entity 목록
   * @param {Object}        [config.mapFeature]   — OpenLayers Feature
   * @param {Object}        [config.geometry]     — OpenLayers Geometry
   * @param {boolean}       [config.isSelected]   — 선택 상태
   */
  constructor(config) {
    if (!config || !config.entityId) {
      throw new Error('SolarEntity: entityId (PanelEntityId) is required');
    }
    if (!(config.entityId instanceof PanelEntityId)) {
      throw new Error('SolarEntity: entityId must be a PanelEntityId instance');
    }

    /** @type {PanelEntityId} — 이 Entity의 식별자 (selfSeq 기준 객체 구분 필수) */
    this.entityId = config.entityId;

    /** @type {string} — 화면 표시명 (기본: entityId.displayLabel) */
    this.name = config.name ?? this.entityId.displayLabel;

    /** @type {Array<SolarEntity>} */
    this.children = config.children ?? [];

    /** @type {string|null} — 부모 Entity의 mapKey */
    this.parentId = config.parentId ?? null;

    // 맵 관련
    this.mapFeature = config.mapFeature ?? null;
    this.geometry = config.geometry ?? null;

    // UI 상태
    this.isSelected = config.isSelected ?? false;

    // ── 경고: backward-compat 필드 (직접 접근보다 entityId 사용 권장) ──
    // 아래 필드는 기존 코드와의 호환성을 위해 유지하되,
    // 신규 코드에서는 항상 this.entityId.* 를 통해 접근할 것
    this._legacyCompat = true;
  }

  // ── Backward-Compatible Getters ──
  // 기존 `this.id` 접근 코드가 깨지지 않도록 getter로 유지

  /** @deprecated entityId.mapKey 사용 권장 */
  get id() {
    return this.entityId.mapKey;
  }

  /** @deprecated entityId.selfDisplayId 사용 권장 */
  get solarModuleId() { return this.entityId.selfDisplayId; }
  /** @deprecated entityId.selfSeq 사용 권장 */
  get solarModuleSeq() { return this.entityId.selfSeq; }
  /** @deprecated entityId.parentDisplayId 사용 권장 */
  get solarStringId() { return this.entityId.parentDisplayId; }
  /** @deprecated entityId.parentSeq 사용 권장 */
  get solarStringSeq() { return this.entityId.parentSeq; }
  /** @deprecated entityId.rootSeq 사용 권장 */
  get solarArraySeq() { return this.entityId.rootSeq; }

  /** @deprecated entityId.entityType 사용 권장 */
  get type() { return this.entityId.entityType; }

  // ── 편의 메서드 ──

  /**
   * Entity 타입에 따른 표시 이름 생성
   * @returns {string}
   */
  getDisplayName() {
    return this.name || this.entityId.displayLabel;
  }

  /**
   * Entity가 선택 가능한지 확인
   * @returns {boolean}
   */
  isSelectable() {
    return this.entityId.entityType === 'string' || this.entityId.entityType === 'module';
  }

  /**
   * Entity의 계층 레벨 반환
   * @returns {number}
   */
  getHierarchyLevel() {
    switch (this.entityId.entityType) {
      case 'array':  return 1;
      case 'string': return 2;
      case 'module': return 3;
      default:       return 0;
    }
  }

  /**
   * 자식 Entity 추가
   * @param {SolarEntity} childEntity
   */
  addChild(childEntity) {
    if (!this.children) {
      this.children = [];
    }
    this.children.push(childEntity);
    childEntity.parentId = this.entityId.mapKey;
  }

  /**
   * 자식 Entity 제거
   * @param {string} childId — child.entityId.mapKey
   */
  removeChild(childId) {
    if (this.children) {
      this.children = this.children.filter(child => child.entityId.mapKey !== childId);
    }
  }

  /**
   * 특정 ID의 자식 Entity 찾기
   * @param {string} childId — child.entityId.mapKey
   * @returns {SolarEntity|null}
   */
  findChild(childId) {
    if (!this.children) return null;
    return this.children.find(child => child.entityId.mapKey === childId) ?? null;
  }

  /**
   * 맵 피처 설정
   * @param {Object} feature
   */
  setMapFeature(feature) {
    this.mapFeature = feature;
    if (feature) {
      feature.set('entityId', this.entityId.mapKey);
      feature.set('entityType', this.entityId.entityType);
      feature.set('entitySeq', this.entityId.selfSeq);
      feature.set('entityDisplayId', this.entityId.selfDisplayId);
    }
  }

  /**
   * 선택 상태 설정
   * @param {boolean} selected
   */
  setSelected(selected) {
    this.isSelected = selected;
    if (this.mapFeature) {
      this.mapFeature.set('isSelected', selected);
    }
  }

  // ── 정적 팩토리 메서드 ──

  /**
   * 기존 OpenLayers Feature에서 SolarEntity 생성
   * @param {import('ol').Feature} feature
   * @returns {SolarEntity}
   */
  static fromFeature(feature) {
    const rawEntityType = feature.get('featureType');
    const name = feature.get('name') || '';
    const entitySeq = feature.get('entitySeq') ?? feature.getId();
    const entityDisplayId = feature.get('entityDisplayId') ?? feature.get('name');

    let entityType = 'string';
    if (rawEntityType === 'Module') {
      entityType = 'module';
    } else if (name.startsWith('Array')) {
      entityType = 'array';
    }

    const parentSeq = readStringSeq(feature);
    const entityId = new PanelEntityId({
      selfSeq: entitySeq,
      selfDisplayId: entityDisplayId,
      entityType,
      hierarchy: {
        parentSeq: parentSeq != null ? Number(parentSeq) : null,
        parentDisplayId: readStringDisplayNo(feature),
        rootSeq: feature.get('solarArrayId') ?? null
      },
      context: 'map-feature'
    });

    return new SolarEntity({
      entityId,
      name,
      mapFeature: feature,
      geometry: feature.getGeometry(),
    });
  }

  /**
   * 기존 solarStrings 데이터에서 SolarEntity 트리 생성 (호환성)
   * @param {Array} solarStrings
   * @returns {{ entities: Map<string,SolarEntity>, rootEntities: SolarEntity[] }}
   */
  static fromSolarStrings(solarStrings) {
    const entityMap = new Map();
    const rootEntities = [];

    solarStrings.forEach(solarString => {
      const stringSeq = Number(solarString.id) || 0;
      const stringDisplayId = String(solarString.stringId ?? '');

      // String Entity 생성
      const stringEntityId = new PanelEntityId({
        selfSeq: stringSeq,
        selfDisplayId: stringDisplayId,
        entityType: 'string',
        hierarchy: {
          rootSeq: Number(solarString.solarArrayId) || null
        },
        context: 'from-solar-strings'
      });

      const stringEntity = new SolarEntity({
        entityId: stringEntityId,
        name: stringDisplayId,
      });

      // Module Entity들 생성
      if (solarString.solarModules) {
        solarString.solarModules.forEach(module => {
          const moduleSeq = Number(module.id) || 0;
          const moduleDisplayId = String(module.moduleId ?? '');

          const moduleEntityId = new PanelEntityId({
            selfSeq: moduleSeq,
            selfDisplayId: moduleDisplayId,
            entityType: 'module',
            hierarchy: {
              parentSeq: stringSeq,
              parentDisplayId: stringDisplayId,
              rootSeq: Number(solarString.solarArrayId) || null
            },
            context: 'from-solar-strings'
          });

          const moduleEntity = new SolarEntity({
            entityId: moduleEntityId,
            name: moduleDisplayId,
            parentId: stringEntity.entityId.mapKey,
          });

          stringEntity.addChild(moduleEntity);
          entityMap.set(moduleEntity.entityId.mapKey, moduleEntity);
        });
      }

      entityMap.set(stringEntity.entityId.mapKey, stringEntity);
    });

    // Array Entity 생성 (solarString의 solarArrayId로 그룹핑)
    const arrayEntities = new Map();
    entityMap.forEach(entity => {
      if (entity.entityId.entityType === 'string' && entity.entityId.rootSeq) {
        const arraySeq = entity.entityId.rootSeq;
        if (!arrayEntities.has(arraySeq)) {
          const arrayId = new PanelEntityId({
            selfSeq: arraySeq,
            selfDisplayId: String(arraySeq),
            entityType: 'array',
            context: 'from-solar-strings'
          });
          const arrayEntity = new SolarEntity({
            entityId: arrayId,
            name: `Array ${arraySeq}`,
            children: []
          });
          arrayEntities.set(arraySeq, arrayEntity);
          rootEntities.push(arrayEntity);
        }
        const arrayEntity = arrayEntities.get(arraySeq);
        entity.parentId = arrayEntity.entityId.mapKey;
        arrayEntity.addChild(entity);
      }
    });

    // 배열이 없으면 기본 Array 생성
    if (rootEntities.length === 0) {
      const defaultArrayId = new PanelEntityId({
        selfSeq: -1,
        selfDisplayId: 'default',
        entityType: 'array',
        context: 'from-solar-strings-fallback'
      });
      const defaultArray = new SolarEntity({
        entityId: defaultArrayId,
        name: 'Default Array',
        children: Array.from(entityMap.values()).filter(e => e.entityId.entityType === 'string'),
      });
      rootEntities.push(defaultArray);
      entityMap.set(defaultArray.entityId.mapKey, defaultArray);
    }

    return { entities: entityMap, rootEntities };
  }
}

export default SolarEntity;
